import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Dropdown } from "../../../../components/dropdown/Dropdown";
import {
  createAccessAssignment,
  updateAccessAssignment,
  getAccessAssignmentById,
  getAllVisitors,
} from "../../../../service/securityService";
import {
  AccessAssignment,
  AccessAssignmentFormData,
  AccessType,
} from "../../../../types/security_types";
import { useOrgUsersStore, useAccessZonesStore } from "../../../../store/store";

interface AccessAssignmentFormProps {
  assignment?: AccessAssignment;
  onSuccess: () => void;
  onCancel: () => void;
}

const AccessAssignmentForm: React.FC<AccessAssignmentFormProps> = ({
  assignment,
  onSuccess,
  onCancel,
}) => {
  const isEdit = !!assignment;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { zonesData, fetchAllZones } = useAccessZonesStore();
  const [visitors, setVisitors] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const [formData, setFormData] = useState<AccessAssignmentFormData>({
    userType: "User",
    userId: "",
    visitorId: "",
    vendorId: "",
    zoneId: "",
    accessType: "Permanent",
    startDateTime: new Date().toISOString().slice(0, 16),
    endDateTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
    approvalAuthority: "",
  });

  useEffect(() => {
    fetchOrgUsers();
    fetchAllZones();
    fetchVisitors();
    if (isEdit && assignment) {
      loadAssignmentData();
    }
  }, [assignment, fetchOrgUsers, fetchAllZones]);

  const fetchVisitors = async () => {
    try {
      const res = await getAllVisitors(undefined, { page: 1, limit: 500 });
      const visitorsData = res.data || [];
      setVisitors(
        visitorsData.map((visitor: any) => {
          const id = visitor._id || visitor.id;
          const name = visitor.fullName || visitor.name;
          return {
            value: String(id),
            label: name || String(id),
          };
        }),
      );
    } catch (error) {
      console.error("Error fetching visitors:", error);
    }
  };

  const loadAssignmentData = async () => {
    if (!assignment) return;
    try {
      setLoading(true);
      const id = assignment._id || assignment.id;
      if (!id) return;
      const assignmentData = await getAccessAssignmentById(id);
      setFormData({
        userType: assignmentData.userType || "User",
        userId: assignmentData.userId || "",
        visitorId: assignmentData.visitorId || "",
        vendorId: assignmentData.vendorId || "",
        zoneId:
          assignmentData.zoneId ||
          (assignmentData.zone ? String(assignmentData.zone) : ""),
        accessType: assignmentData.accessType || "Permanent",
        startDateTime: assignmentData.startDateTime
          ? new Date(assignmentData.startDateTime).toISOString().slice(0, 16)
          : new Date().toISOString().slice(0, 16),
        endDateTime: assignmentData.endDateTime
          ? new Date(assignmentData.endDateTime).toISOString().slice(0, 16)
          : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
              .toISOString()
              .slice(0, 16),
        approvalAuthority: assignmentData.approvalAuthority || "",
      });
    } catch (error: any) {
      console.error("Error loading assignment data:", error);
      toast.error("Failed to load assignment data");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.userType === "User" && !formData.userId) {
      newErrors.userId = "User is required";
    }

    if (formData.userType === "Visitor" && !formData.visitorId) {
      newErrors.visitorId = "Visitor is required";
    }

    if (formData.userType === "Vendor" && !formData.vendorId) {
      newErrors.vendorId = "Vendor is required";
    }

    if (!formData.zoneId) {
      newErrors.zoneId = "Zone is required";
    }

    if (!formData.startDateTime) {
      newErrors.startDateTime = "Start date is required";
    }

    if (!formData.endDateTime) {
      newErrors.endDateTime = "End date is required";
    }

    if (
      formData.startDateTime &&
      formData.endDateTime &&
      new Date(formData.endDateTime) <= new Date(formData.startDateTime)
    ) {
      newErrors.endDateTime = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);
      const selectedZone = zonesData.find(
        (z: any) => String(z._id || z.id) === String(formData.zoneId),
      );
      const zoneName = selectedZone?.zoneName;

      const submitData = {
        ...formData,
        startDateTime: new Date(formData.startDateTime).toISOString(),
        endDateTime: new Date(formData.endDateTime).toISOString(),
        zoneName,
      };

      if (isEdit && assignment) {
        const id = assignment._id || assignment.id;
        if (!id) return;
        await updateAccessAssignment(id, submitData);
        toast.success("Access assignment updated successfully");
      } else {
        await createAccessAssignment(submitData);
        toast.success("Access assignment created successfully");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Error saving access assignment:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to save access assignment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
        {isEdit
          ? "Update Access Assignment Details"
          : "Create New Access Assignment"}
      </h2>
      <form
        onSubmit={handleSubmit}
        id="accessAssignmentForm"
        className="px-6 pb-6"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              User Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.userType}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  userType: e.target.value as "User" | "Visitor" | "Vendor",
                  userId: "",
                  visitorId: "",
                  vendorId: "",
                });
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
              required
            >
              <option value="User">User</option>
              <option value="Visitor">Visitor</option>
              <option value="Vendor">Vendor</option>
            </select>
          </div>

          {formData.userType === "User" && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                User <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.userId || ""}
                onChange={(e) => {
                  setFormData({ ...formData, userId: e.target.value });
                  setErrors({ ...errors, userId: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="">Select user</option>
                {orgUsersData.map((user: any) => {
                  const fullName =
                    `${user.firstName || ""} ${user.lastName || ""}`.trim();
                  const display = fullName || user.name || user._id;
                  return (
                    <option key={user._id} value={user._id}>
                      {display}
                    </option>
                  );
                })}
              </select>
              {errors.userId && (
                <span className="text-red-500 text-xs">{errors.userId}</span>
              )}
            </div>
          )}

          {formData.userType === "Visitor" && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Visitor <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.visitorId || ""}
                onChange={(e) => {
                  setFormData({ ...formData, visitorId: e.target.value });
                  setErrors({ ...errors, visitorId: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.visitorId ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="">Select visitor</option>
                {visitors.map((visitor) => (
                  <option key={visitor.value} value={visitor.value}>
                    {visitor.label}
                  </option>
                ))}
              </select>
              {errors.visitorId && (
                <span className="text-red-500 text-xs">{errors.visitorId}</span>
              )}
            </div>
          )}

          {formData.userType === "Vendor" && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Vendor <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.vendorId || ""}
                onChange={(e) => {
                  setFormData({ ...formData, vendorId: e.target.value });
                  setErrors({ ...errors, vendorId: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.vendorId ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter vendor ID or name"
                required
              />
              {errors.vendorId && (
                <span className="text-red-500 text-xs">{errors.vendorId}</span>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Zone <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.zoneId}
              onChange={(e) => {
                setFormData({ ...formData, zoneId: e.target.value });
                setErrors({ ...errors, zoneId: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.zoneId ? "border-red-500" : "border-gray-300"
              }`}
              required
            >
              <option value="">Select zone</option>
              {zonesData.map((zone: any) => (
                <option key={zone._id || zone.id} value={zone._id || zone.id}>
                  {zone.zoneName}
                </option>
              ))}
            </select>
            {errors.zoneId && (
              <span className="text-red-500 text-xs">{errors.zoneId}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Access Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.accessType}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  accessType: e.target.value as AccessType,
                });
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
              required
            >
              <option value="Permanent">Permanent</option>
              <option value="Temporary">Temporary</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Start Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.startDateTime}
                onChange={(e) => {
                  setFormData({ ...formData, startDateTime: e.target.value });
                  setErrors({ ...errors, startDateTime: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.startDateTime ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.startDateTime && (
                <span className="text-red-500 text-xs">
                  {errors.startDateTime}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                End Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.endDateTime}
                onChange={(e) => {
                  setFormData({ ...formData, endDateTime: e.target.value });
                  setErrors({ ...errors, endDateTime: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.endDateTime ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.endDateTime && (
                <span className="text-red-500 text-xs">
                  {errors.endDateTime}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Approval authority
              <span className="text-gray-400 font-normal text-xs ml-1">
                (required for Critical zones)
              </span>
            </label>
            <input
              type="text"
              value={formData.approvalAuthority}
              onChange={(e) => {
                setFormData({ ...formData, approvalAuthority: e.target.value });
                setErrors({ ...errors, approvalAuthority: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.approvalAuthority ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Name or role for audit trail"
            />
            {errors.approvalAuthority && (
              <span className="text-red-500 text-xs">
                {errors.approvalAuthority}
              </span>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default AccessAssignmentForm;
